import React, { createRef, MouseEvent, ReactElement, useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { withRouter } from 'react-router-dom';
import { IPayButton } from './PayButton.types';
import { AuthorizationType, PaymentButtonValues, PaymentCategoryType } from '../../consts';
import {
  ButtonWrapper,
  CenterText,
  CheckboxWrapper,
  InnerButtonWrapper,
  LoaderWrapper,
  PaymentCardWrapper,
} from './PayButton.styles';
import Spinner from '../spinner/Spinner';
import PaymentGPayButton from './paymentGPayButton/PaymentGPayButton';
import PaymentButton from './paymentButton/PaymentButton';
import { RecoveryPaymentButton } from '../recoveryPaymentButton/RecoveryPaymentButton';
import RecoveryPaymentButtonStyleType from '../recoveryPaymentButton/RecoveryPaymentButton.types';
import withLocaleContext from '../../hoc/withLocaleContext';
import withSaferPayScriptContext from '../../hoc/WithSaferPayScriptContext';
import withContext from '../../hoc/withContext';
import ApplePayButton from './applePayButton/ApplePayButton';
import SaveInstrumentCheckbox from './paymentButton/saveIntrumentCheckbox/SaveInstrumentCheckbox';
import withSelectedMethodContext from '../../hoc/withSelectedMethodContext';
import { APP_STATUS, AppState, SelectPaymentMethodChangeStatus } from '../../config/config';
import PaymentMethodChangeButton from '../recoveryPaymentButton/changePaymentMethodButton/ChangePaymentMethodButton';
import withAppStateContext from '../../hoc/withAppStateContext';

const PAYMENT_BUTTON_MIN_HEIGHT = 48;

function PayButton(props: IPayButton): ReactElement {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isProcessedPayment, setIsProcessedPayment] = useState<boolean>(false);
  const [isSaveInstrumentChecked, setIsSaveInstrumentChecked] = useState<boolean>(false);
  const buttonWrapperRef = createRef<HTMLDivElement>();
  const spinnerWrapperRef = createRef<HTMLDivElement>();
  const innerButtonsRef = createRef<HTMLDivElement>();
  const [isChangePaymentMethodButtonVisible, setIsChangePaymentMethodButtonVisible] = useState<boolean>(false);

  const setLoading = (isComponentLoading: boolean) => {
    setIsLoading(isComponentLoading);
  };

  const handleProcessedPayment = (isComponentLoading: boolean) => {
    setIsProcessedPayment(true);
    setLoading(isComponentLoading);
  };

  const handleChangeCheckbox = (event: MouseEvent) => {
    event.stopPropagation();
    setIsSaveInstrumentChecked(!isSaveInstrumentChecked);
  };

  const isNotBlikRedirectOrCard = (
    selectedPaymentMethodType: string | undefined,
    authorizationType: string | undefined
  ) =>
    !(
      (selectedPaymentMethodType === PaymentCategoryType.BLIK && authorizationType === AuthorizationType.CODE) ||
      selectedPaymentMethodType === PaymentCategoryType.CARD
    );

  useEffect(() => {
    const { payment, selectPaymentMethodChangeStatus } = props;
    const { status, selectedPaymentMethodId, selectedPaymentMethodType, authorizationType } = payment;

    const isChangeMethodButtonVisible = (status === APP_STATUS.NEW &&
      selectedPaymentMethodId &&
      selectPaymentMethodChangeStatus !== SelectPaymentMethodChangeStatus.CHANGED &&
      isNotBlikRedirectOrCard(selectedPaymentMethodType, authorizationType)) as boolean;

    setIsChangePaymentMethodButtonVisible(isChangeMethodButtonVisible);
  }, []);

  const getRenderProperties = () => {
    const {
      progress,
      isTokenizationEnabled,
      isTokenizedPayment,
      payment,
      selectPaymentMethodChangeStatus,
      appState,
    } = props;
    const { redirectUrl = '', pendingRecoveryEnabled } = payment;
    const isVisible = progress === PaymentButtonValues.SHOW && !isLoading && appState === AppState.PAYMENT;

    const isRecoveryPaymentButtonEnabled =
      isVisible &&
      ((redirectUrl && pendingRecoveryEnabled) ||
        selectPaymentMethodChangeStatus === SelectPaymentMethodChangeStatus.CAN_BE_CHANGED);

    const isSaveInstrumentAvailable = (isVisible &&
      !isLoading &&
      isTokenizationEnabled &&
      !isTokenizedPayment) as boolean;

    return { isVisible, isRecoveryPaymentButtonEnabled, isSaveInstrumentAvailable };
  };

  const {
    payment,
    paymentMethodId,
    paymentId,
    paymentMethodType,
    showPaymentNotification,
    showPaymentRedirectScreen,
    handleError,
    setOrderDescriptionVisibility,
    isTokenizationEnabled,
    selectPaymentMethodChangeStatus,
    paymentButtonRef,
  } = props;

  if (!payment || Object.keys(payment).length === 0) {
    return <></>;
  }

  const { isVisible, isRecoveryPaymentButtonEnabled, isSaveInstrumentAvailable } = getRenderProperties();

  const { redirectUrl = '', pendingRecoveryEnabled } = payment;
  const paymentData = {
    payment,
    paymentId,
    paymentMethodId,
    paymentMethodType,
  };

  useEffect(() => {
    const { setPayButtonComponentHeight } = props;
    if (setPayButtonComponentHeight) {
      if (isLoading) {
        setPayButtonComponentHeight(spinnerWrapperRef.current?.clientHeight ?? 0);
      } else {
        const innerButtonsHeight = (innerButtonsRef.current?.clientHeight as number) || 0;
        setPayButtonComponentHeight(Math.max(innerButtonsHeight as number, PAYMENT_BUTTON_MIN_HEIGHT));
      }
    }
  }, [isLoading, innerButtonsRef]);

  const getPaymentButton = () => (
    <PaymentButton
      isVisible={isVisible}
      {...paymentData}
      innerRef={paymentButtonRef}
      defaultRedirectUrl={redirectUrl}
      showPaymentNotification={showPaymentNotification}
      showPaymentRedirectScreen={showPaymentRedirectScreen}
      setLoading={setLoading}
      setOrderDescriptionVisibility={setOrderDescriptionVisibility}
      handleError={handleError}
      handleProcessedPayment={handleProcessedPayment}
      isSaveInstrumentChecked={isSaveInstrumentChecked}
    />
  );

  const getSaveInstrumentCheckbox = () => (
    <CheckboxWrapper>
      <SaveInstrumentCheckbox
        classNamePrefix="payment-button-save_instrument"
        value={isSaveInstrumentChecked}
        onClick={handleChangeCheckbox}
      />
    </CheckboxWrapper>
  );

  const getRecoveryButton = () => (
    <>
      <CenterText>
        <FormattedMessage id="statusBar.or" />
      </CenterText>

      {payment.redirectUrl ? (
        <RecoveryPaymentButton
          isRecoveryEnabled={pendingRecoveryEnabled}
          paymentId={paymentId}
          type={RecoveryPaymentButtonStyleType.PENDING}
          message="pay.changePaymentMethod"
          className="statusbar__pending_recovery_button"
          onClick={() => setLoading(true)}
          onError={handleError}
        />
      ) : (
        <PaymentMethodChangeButton
          className="statusbar__change_payment_button"
          isVisible={isChangePaymentMethodButtonVisible}
        />
      )}
    </>
  );

  const getConfirmPaymentButton = () => {
    switch (paymentMethodType) {
      case PaymentCategoryType.GOOGLE_PAY:
        if (!redirectUrl) {
          return (
            <PaymentGPayButton
              isVisible={isVisible}
              setOrderDescriptionVisibility={setOrderDescriptionVisibility}
              {...paymentData}
            />
          );
        }
        return getPaymentButton();

      case PaymentCategoryType.APPLE_PAY:
        if (!redirectUrl) {
          return (
            <ApplePayButton
              isVisible={isVisible}
              setOrderDescriptionVisibility={setOrderDescriptionVisibility}
              {...paymentData}
            />
          );
        }
        return getPaymentButton();

      case PaymentCategoryType.CARD:
        return (
          <PaymentCardWrapper>
            {isSaveInstrumentAvailable && getSaveInstrumentCheckbox()}
            {getPaymentButton()}
          </PaymentCardWrapper>
        );

      case PaymentCategoryType.BLIK:
        if (!isProcessedPayment) {
          return getPaymentButton();
        }
        return <></>;

      default:
        return getPaymentButton();
    }
  };

  return (
    <ButtonWrapper
      isRecoveryEnabled={
        isVisible &&
        (pendingRecoveryEnabled || selectPaymentMethodChangeStatus === SelectPaymentMethodChangeStatus.CAN_BE_CHANGED)
      }
      isTokenizationEnabled={isTokenizationEnabled}
      ref={buttonWrapperRef}
    >
      {isLoading ? (
        <LoaderWrapper className="payment-button-loader" height={PAYMENT_BUTTON_MIN_HEIGHT} ref={spinnerWrapperRef}>
          <Spinner isLoading={isLoading} />
        </LoaderWrapper>
      ) : (
        <InnerButtonWrapper>
          <div ref={innerButtonsRef}>
            {getConfirmPaymentButton()}
            {isRecoveryPaymentButtonEnabled && getRecoveryButton()}
          </div>
        </InnerButtonWrapper>
      )}
    </ButtonWrapper>
  );
}

export default withSelectedMethodContext(
  withSelectedMethodContext(
    withLocaleContext(withSaferPayScriptContext(withContext(withRouter(withAppStateContext(PayButton)))))
  )
);
